import { MouseEventHandler, useMemo } from 'react';
import { css, cx } from '@linaria/atomic';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { splitStylesProp } from '../../../../helpers';
import { Chip, TagChipProps } from '../../../chip';
import { PopoverMenu, PopoverMenuItem, usePopoverMenu } from '../../../popover';
import { Text } from '../../../text';
import { useChatBubbleContext } from '../../chat';
import { BubbleTag, BubbleTagAction } from '../../types';

const tagIconStyle1 = css`
  transition: transform 0.2s ease-in-out;
`;

const openIconStyle = css`
  transform: rotate(180deg);
`;

const closedIconStyle = css`
  transform: rotate(0deg);
`;

const tagDivStyle2 = css`
  display: flex;
  flex-direction: column;
`;

const inboundTagDivStyle = css`
  align-items: flex-start;
`;

const outboundTagDivStyle = css`
  align-items: flex-end;
`;

const tagTextStyle3 = css`
  padding: ${theme.spacing(1, 2)};
`;

const tagTextStyle4 = css`
  cursor: 'pointer';
`;

type RightElementProps = Pick<TagChipProps<MouseEventHandler<HTMLButtonElement>>, 'rightElement'>;
type TagsProps = BubbleTag & {
  actions?: BubbleTagAction[];
  leftIcon?: React.ReactNode;
  actionHeading?: string;
  children?: React.ReactNode;
};
export const Tag = ({
  color,
  onClick,
  onRemoveClick,
  trackingId,
  label,
  actions,
  leftIcon,
  actionHeading,
  children,
}: TagsProps) => {
  const { getItemProps, getTriggerProps, getMenuProps, isOpen } = usePopoverMenu({
    placement: 'bottom-start',
  });

  const { direction } = useChatBubbleContext();
  const hasActions = !!actions?.length;

  const rightElementProps = useMemo<RightElementProps>(
    () =>
      hasActions
        ? {
            rightElement: (
              <Icon name='caret-down-tiny' className={cx(tagIconStyle1, isOpen ? openIconStyle : closedIconStyle)} />
            ),
          }
        : {},
    [hasActions, Icon, isOpen]
  );

  return (
    <>
      <div
        className={cx('tag-buttons', tagDivStyle2, direction === 'inbound' ? inboundTagDivStyle : outboundTagDivStyle)}
      >
        <Chip.Tag
          color={color}
          onClick={hasActions ? getTriggerProps().onClick : onClick}
          isRemovable={!hasActions}
          onRemoveClick={hasActions ? undefined : onRemoveClick}
          leftElement={leftIcon}
          {...rightElementProps}
          trackingId={trackingId}
          ref={getTriggerProps().ref}
        >
          {label}
        </Chip.Tag>
        {children}
      </div>
      {actions && (
        <PopoverMenu {...getMenuProps()}>
          {actionHeading && <Text className={tagTextStyle3}>{actionHeading}</Text>}
          {actions.map(({ label, onClick, icon, trackingId, css: styleProps }, index) => {
            const { className, css } = splitStylesProp(styleProps);
            return (
              <PopoverMenuItem
                key={`chat-item-more-action-${trackingId ?? index}`}
                {...getItemProps({
                  index,
                  onClick,
                })}
                className={cx(tagTextStyle4, className)}
                css={css}
                trackingId={trackingId}
              >
                {icon}
                {label}
              </PopoverMenuItem>
            );
          })}
        </PopoverMenu>
      )}
    </>
  );
};
Tag.displayName = 'ChatTag';
